/**
 * Last collection action per customer, per business — the web counterpart of
 * Android's CollectionActionLocalSource (a local table written by
 * RecordCollectionAction whenever the merchant reminds, calls, sets a due date
 * on… a customer). It feeds the "Recommended" sort's anti-nag cooldown: a
 * customer acted on recently sinks in the list.
 *
 * Device-local on Android, browser-local here: one key per business holding
 * { customerId: lastActionEpochMillis }.
 */

const KEY_PREFIX = 'okc_collection_actions'

export type CollectionActionChannel =
  | 'DUE_DATE'
  | 'CALL'
  | 'WHATSAPP'
  | 'SMS'
  | 'REPORT'
  | 'DEFAULTER'
  | 'VOICE'
  | 'BULK_REMINDER'

type Listener = () => void
const listeners = new Set<Listener>()

export const subscribeCollectionActions = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

const key = (businessId: string) => `${KEY_PREFIX}:${businessId}`

export const getLastActionByCustomer = (businessId: string): Record<string, number> => {
  const store = storage()
  if (!store || !businessId) {
    return {}
  }
  try {
    const parsed = JSON.parse(store.getItem(key(businessId)) || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export const recordCollectionAction = (
  businessId: string,
  customerIds: string | string[],
  // Kept for parity/analytics symmetry with Android; the score only needs the time
  _channel: CollectionActionChannel,
  actionTime = Date.now()
): void => {
  const store = storage()
  const ids = (Array.isArray(customerIds) ? customerIds : [customerIds]).filter(Boolean)
  if (!store || !businessId || ids.length === 0) {
    return
  }
  const current = getLastActionByCustomer(businessId)
  ids.forEach((id) => {
    current[id] = Math.max(current[id] || 0, actionTime)
  })
  try {
    store.setItem(key(businessId), JSON.stringify(current))
  } catch {}
  listeners.forEach((listener) => listener())
}
